import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, CreditCard, Landmark, Banknote } from 'lucide-react';
interface OrderData {
  orderNumber: string;
  totalPrice: number;
  name: string;
  pickupAddress: string;
  pickupCity: string;
  deliveryAddress: string;
  deliveryCity: string;
  moveDate: string;
}
const Confirmation = () => {
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>('card');
  useEffect(() => {
    // Get order data from localStorage
    const storedOrder = localStorage.getItem('moveOrder');
    if (storedOrder) {
      setOrderData(JSON.parse(storedOrder));
    } else {
      // Redirect to order form if no order data exists
      navigate('/order');
    }
  }, [navigate]);
  const handlePaymentSelect = (method: string) => {
    setPaymentMethod(method);
  };
  const handlePaymentSubmit = () => {
    // In a real app, this would process the payment
    // For this demo, we'll just update the status and redirect to tracking
    if (orderData) {
      localStorage.setItem('moveOrder', JSON.stringify({
        ...orderData,
        status: 'Confirmed',
        paymentMethod
      }));
      navigate('/tracking');
    }
  };
  if (!orderData) {
    return <div className="min-h-screen bg-gray-50 py-10" data-id="element-86">
        <div className="container mx-auto px-4" data-id="element-87">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm p-6 text-center" data-id="element-88">
            <p data-id="element-89">Loading order details...</p>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 py-10" data-id="element-90">
      <div className="container mx-auto px-4" data-id="element-91">
        <div className="max-w-2xl mx-auto" data-id="element-92">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-93">
            <div className="text-center mb-8" data-id="element-94">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4" data-id="element-95">
                <CheckCircle className="h-8 w-8 text-green-500" data-id="element-96" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-97">Booking Complete!</h1>
              <p className="text-gray-600 mt-2" data-id="element-98">
                Your order number is{' '}
                <span className="font-semibold" data-id="element-99">{orderData.orderNumber}</span>
              </p>
            </div>
            <div className="border-t border-gray-200 pt-6 mb-6" data-id="element-100">
              <h2 className="text-xl font-semibold mb-4" data-id="element-101">Order Summary</h2>
              <div className="space-y-4" data-id="element-102">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-103">
                  <div data-id="element-104">
                    <p className="text-sm text-gray-500 mb-1" data-id="element-105">From</p>
                    <p className="font-medium" data-id="element-106">
                      {orderData.pickupAddress}, {orderData.pickupCity}
                    </p>
                  </div>
                  <div data-id="element-107">
                    <p className="text-sm text-gray-500 mb-1" data-id="element-108">To</p>
                    <p className="font-medium" data-id="element-109">
                      {orderData.deliveryAddress}, {orderData.deliveryCity}
                    </p>
                  </div>
                </div>
                <div data-id="element-110">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-111">Moving Date</p>
                  <p className="font-medium" data-id="element-112">
                    {new Date(orderData.moveDate).toLocaleDateString()}
                  </p>
                </div>
                <div data-id="element-113">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-114">Contact</p>
                  <p className="font-medium" data-id="element-115">{orderData.name}</p>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-6 mb-6" data-id="element-116">
              <h2 className="text-xl font-semibold mb-4" data-id="element-117">Payment Method</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-118">
                <button className={`flex flex-col items-center justify-center p-4 border rounded-lg ${paymentMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`} onClick={() => handlePaymentSelect('card')} data-id="element-119">
                  <CreditCard className={`h-8 w-8 mb-2 ${paymentMethod === 'card' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-120" />
                  <span className={paymentMethod === 'card' ? 'text-blue-600 font-medium' : 'text-gray-500'} data-id="element-121">
                    Credit Card
                  </span>
                </button>
                <button className={`flex flex-col items-center justify-center p-4 border rounded-lg ${paymentMethod === 'bank' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`} onClick={() => handlePaymentSelect('bank')} data-id="element-122">
                  <Landmark className={`h-8 w-8 mb-2 ${paymentMethod === 'bank' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-123" />
                  <span className={paymentMethod === 'bank' ? 'text-blue-600 font-medium' : 'text-gray-500'} data-id="element-124">
                    Bank Link
                  </span>
                </button>
                <button className={`flex flex-col items-center justify-center p-4 border rounded-lg ${paymentMethod === 'cash' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`} onClick={() => handlePaymentSelect('cash')} data-id="element-125">
                  <Banknote className={`h-8 w-8 mb-2 ${paymentMethod === 'cash' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-126" />
                  <span className={paymentMethod === 'cash' ? 'text-blue-600 font-medium' : 'text-gray-500'} data-id="element-127">
                    Cash
                  </span>
                </button>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-6" data-id="element-128">
              <div className="flex justify-between items-center mb-6" data-id="element-129">
                <span className="text-lg font-semibold" data-id="element-130">Total Amount:</span>
                <span className="text-2xl font-bold" data-id="element-131">
                  €{orderData.totalPrice}
                </span>
              </div>
              <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-132">
                <Link to="/tracking" className="text-blue-600 mb-4 md:mb-0" data-id="element-133">
                  Track your order
                </Link>
                <button onClick={handlePaymentSubmit} className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center" data-id="element-134">
                  {paymentMethod === 'cash' ? 'Confirm Payment on Delivery' : 'Pay Now'}
                  <ArrowRight className="ml-2 h-5 w-5" data-id="element-135" />
                </button>
              </div>
            </div>
          </div>
          <div className="text-center" data-id="element-136">
            <p className="text-gray-500 mb-4" data-id="element-137">
              Need help with your booking? Contact our customer support at{' '}
              <span className="font-medium" data-id="element-138">+372 5555 1234</span>
            </p>
            <Link to="/" className="text-blue-600 hover:underline" data-id="element-139">
              Return to Homepage
            </Link>
          </div>
        </div>
      </div>
    </div>;
};
export default Confirmation;